import { Link } from "react-router-dom";
import PublicShell from "@/components/public/PublicShell";
import SEO from "@/components/SEO";
import { ArrowRight } from "lucide-react";

/**
 * Catch-all 404 page.
 *
 * Replaces the previous `<Navigate to="/" replace />` catch-all that made
 * every unknown URL render the homepage at HTTP 200 with `index,follow`
 * and `canonical=/` — a classic Soft-404 that Google flags.  This page
 * renders distinct 404 content with `noindex,follow` so crawlers stop
 * treating unknown paths as thin duplicate homepages, while humans get a
 * graceful recovery screen.  The genuine /marketplace/{slug} unknown-slug
 * behaviour (true HTTP 404 from the backend prerender) is unchanged.
 */
export default function NotFound() {
  return (
    <PublicShell>
      <SEO
        title="Page Not Found"
        description="The page you're looking for isn't available. Explore the Vanturis marketplace of luxury motorcoaches, Prevost motorhomes and specialty vehicles."
        path="/404"
        robots="noindex,follow"
        noIndex
      />
      <section
        className="container-editorial py-24 md:py-32 max-w-2xl text-center"
        data-testid="not-found-page"
      >
        <p className="overline mb-4">Error 404</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl text-ink leading-tight">
          This page can’t be found.
        </h1>
        <p className="mt-6 text-ink-100 text-lg leading-relaxed">
          The page you requested may have been moved, retired, or never existed.
          Explore the curated Vanturis marketplace instead.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link
            to="/marketplace"
            data-testid="not-found-marketplace-link"
            className="inline-flex items-center gap-2 bg-ink text-ivory px-6 py-3 text-sm uppercase tracking-[0.18em] hover:bg-ink-100 transition-colors"
          >
            Browse the Marketplace <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <Link
            to="/"
            data-testid="not-found-home-link"
            className="inline-flex items-center gap-2 border border-ink text-ink px-6 py-3 text-sm uppercase tracking-[0.18em] hover:bg-ink hover:text-ivory transition-colors"
          >
            Return Home
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}
